/**
 * Catalogue data loaders — observation definitions, activity definitions,
 * plan definitions, and the goal category / sub-category hierarchy.
 *
 * Each function returns fresh data structures rather than mutating module
 * globals. The caller is responsible for triggering renders after the
 * data is in place.
 *
 * **Canonical shape only.** The coexistence layer that handled both
 * pre-OMON-079 and post-reseed shapes was removed in OMON-085 Stage A
 * (2026-05-11) once the 2026-05-10 reseed-v4 was confirmed stable. The
 * legacy `_catalogueCategory` + `_catalogueAim` collections are now empty
 * in production. The post-reseed canonical shape is what we read:
 *
 *   - `_observationDefinition.active: boolean` (no `status` string fallback)
 *   - `_goalCategory` + `_goalSubcategory` collections (no `_catalogueCategory`
 *     / `_catalogueAim` legacy reads)
 *   - `_goalSubcategory` doc IDs are composite `<categoryCode>__<subcategoryCode>`
 *
 * Stage B (drop the matching legacy *writes* in
 * `agent.fhir/scripts/reseed_catalogue_v4.py`) is a separate PR after one
 * deploy cycle confirms no regression — see OMON-085 ticket.
 *
 * Extracted from main.ts as part of OMON-031 Phase 4.
 */

import { collection, doc, getDoc, getDocs, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { db } from "../firebase";
import type { LiveChange } from "./live-maps";
import type {
  ObservationDefinition,
  ActivityDefinition,
  GoalCategoryNode,
  GoalSubcategoryNode,
} from "../types";

export async function loadCatalogue(): Promise<Map<string, ObservationDefinition>> {
  const snap = await getDocs(collection(db, "_observationDefinition"));
  const out = new Map<string, ObservationDefinition>();
  snap.forEach((d) => {
    const data = d.data() as ObservationDefinition & { active?: boolean };
    if (data.active !== true) return;
    out.set(d.id, data);
  });
  return out;
}

/**
 * Load only the named observation definitions — what ONE plan needs.
 *
 * The full `loadCatalogue` is ~230 documents in training and more in
 * production; a plan references a handful. Measured on an iPhone
 * (2026-07-31), the full catalogue queued other reads for minutes, so opening
 * the first plan of a session took over a minute on a phone (Ed, 2026-09-17).
 *
 * ☠️ Filters exactly as `loadCatalogue` does (`active === true`). A targeted
 * fetch that kept an inactive definition would show a plan something the full
 * catalogue hides, and the answer would change depending on which load ran
 * first. Missing ids are simply absent, as they are in the full map.
 */
export async function loadDefinitionsByIds(
  ids: string[],
): Promise<Map<string, ObservationDefinition>> {
  const snaps = await Promise.all(
    ids.map((id) => getDoc(doc(db, "_observationDefinition", id))),
  );
  const out = new Map<string, ObservationDefinition>();
  snaps.forEach((d) => {
    if (!d.exists()) return;
    const data = d.data() as ObservationDefinition & { active?: boolean };
    if (data.active !== true) return;
    out.set(d.id, data);
  });
  return out;
}

export async function loadActivities(): Promise<Map<string, ActivityDefinition>> {
  const snap = await getDocs(collection(db, "_activityDefinition"));
  const out = new Map<string, ActivityDefinition>();
  snap.forEach((d) => {
    const data = d.data() as ActivityDefinition & { active?: boolean };
    if (data.active !== true) return;
    out.set(d.id, data);
  });
  return out;
}

/**
 * Load the full Goal Category + Goal Sub-category hierarchy.
 *
 * Reads `_goalCategory` + `_goalSubcategory` (the post-OMON-079 canonical
 * collections). `_goalSubcategory` doc IDs are composite
 * `<categoryCode>__<subcategoryCode>` — same shape the consumer lookup
 * key uses (see `resolvers.ts`).
 */
export async function loadHierarchy(): Promise<{
  categories: Map<string, GoalCategoryNode>;
  subcategories: Map<string, GoalSubcategoryNode>;
}> {
  const [catsSnap, subsSnap] = await Promise.all([
    getDocs(collection(db, "_goalCategory")),
    getDocs(collection(db, "_goalSubcategory")),
  ]);

  const categories = new Map<string, GoalCategoryNode>();
  catsSnap.forEach((d) => {
    const data = d.data() as { code: string; display: string; order: number; active?: boolean };
    categories.set(data.code, {
      code: data.code,
      display: data.display,
      order: data.order,
      status: data.active === false ? "retired" : "active",
    });
  });

  const subcategories = new Map<string, GoalSubcategoryNode>();
  subsSnap.forEach((d) => {
    const data = d.data() as {
      code: string;
      display: string;
      goalCategoryCode: string;
      order: number;
      active?: boolean;
    };
    subcategories.set(d.id, {
      code: data.code,
      display: data.display,
      goalCategoryCode: data.goalCategoryCode,
      order: data.order,
      status: data.active === false ? "retired" : "active",
    });
  });

  return { categories, subcategories };
}

// OMON-102: live-listener counterparts to the one-shot loaders above.
//
// Lifecycle (2026-09-24 perf): attached ONCE per session on the first visit
// to Goals Inventory and kept until sign-out (see `ensureCatalogueLive` in
// ensure.ts). They used to be torn down on leaving the tab, so every
// re-entry re-downloaded all ~690 documents on top of `ensureCatalogue()`.
//
// Each keyed listener reports its FIRST snapshot as the full entry set (the
// caller fills its Map in place) and every later snapshot as `docChanges()`
// deltas, so the state Maps are never replaced (the ensure.ts invariant).

/** What a keyed-collection listener hands its caller. */
export type LiveUpdate<V> =
  | { first: true; entries: Map<string, V> }
  | { first: false; changes: LiveChange<V>[] };

/**
 * Listen to a collection whose Map is keyed by doc id and holds only
 * `active === true` documents — the same filter as `loadCatalogue` /
 * `loadActivities`, so a live update never shows something the one-shot load
 * would hide. A doc flipped to inactive is removed.
 */
function subscribeActiveById<V>(
  collectionName: string,
  onUpdate: (u: LiveUpdate<V>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  let first = true;
  return onSnapshot(
    collection(db, collectionName),
    (snap) => {
      if (first) {
        first = false;
        const entries = new Map<string, V>();
        snap.forEach((d) => {
          const data = d.data() as V & { active?: boolean };
          if (data.active === true) entries.set(d.id, data);
        });
        onUpdate({ first: true, entries });
        return;
      }
      const changes: LiveChange<V>[] = snap.docChanges().map((c) => {
        const data = c.doc.data() as V & { active?: boolean };
        return c.type === "removed" || data.active !== true
          ? { type: "remove", key: c.doc.id }
          : { type: "upsert", key: c.doc.id, value: data };
      });
      onUpdate({ first: false, changes });
    },
    onError,
  );
}

export function subscribeCatalogue(
  onUpdate: (u: LiveUpdate<ObservationDefinition>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return subscribeActiveById<ObservationDefinition>("_observationDefinition", onUpdate, onError);
}

export function subscribeActivities(
  onUpdate: (u: LiveUpdate<ActivityDefinition>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return subscribeActiveById<ActivityDefinition>("_activityDefinition", onUpdate, onError);
}

/**
 * Hierarchy needs both `_goalCategory` AND `_goalSubcategory`; each has its
 * own listener but updates fire as one combined callback. Emits the FULL maps
 * (tens of docs — a delta isn't worth it, and categories are keyed by `code`
 * rather than doc id) whenever both have fired at least once; the caller
 * fills its Maps in place.
 */
export function subscribeHierarchy(
  onUpdate: (data: {
    categories: Map<string, GoalCategoryNode>;
    subcategories: Map<string, GoalSubcategoryNode>;
  }) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  let cats: Map<string, GoalCategoryNode> | null = null;
  let subs: Map<string, GoalSubcategoryNode> | null = null;
  const emit = () => {
    if (cats && subs) onUpdate({ categories: cats, subcategories: subs });
  };

  const unsubCats = onSnapshot(
    collection(db, "_goalCategory"),
    (snap) => {
      const out = new Map<string, GoalCategoryNode>();
      snap.forEach((d) => {
        const data = d.data() as { code: string; display: string; order: number; active?: boolean };
        out.set(data.code, {
          code: data.code,
          display: data.display,
          order: data.order,
          status: data.active === false ? "retired" : "active",
        });
      });
      cats = out;
      emit();
    },
    onError,
  );

  const unsubSubs = onSnapshot(
    collection(db, "_goalSubcategory"),
    (snap) => {
      const out = new Map<string, GoalSubcategoryNode>();
      snap.forEach((d) => {
        const data = d.data() as {
          code: string;
          display: string;
          goalCategoryCode: string;
          order: number;
          active?: boolean;
        };
        out.set(d.id, {
          code: data.code,
          display: data.display,
          goalCategoryCode: data.goalCategoryCode,
          order: data.order,
          status: data.active === false ? "retired" : "active",
        });
      });
      subs = out;
      emit();
    },
    onError,
  );

  return () => {
    unsubCats();
    unsubSubs();
  };
}

